import { content } from '../../content';
import { getEventChoiceUnavailableReason, resolveCardDefinition } from '../../core/engine';
import type { CardInstance, EventEffect, GameState, Screen } from '../../core/types';
import type { CommandHandler } from '../App';
import { CardView } from '../components/CardView';
import { GameHeader } from '../components/GameHeader';

type ExpeditionSystem = Extract<Screen, 'event' | 'trader' | 'forge'>;

function gearCards(state: GameState): CardInstance[] {
  return (
    state.run?.deck.filter((card) => content.cards[card.definitionId]?.gear) ?? []
  );
}

function describeEventEffect(effect: EventEffect, state: GameState): string {
  switch (effect.kind) {
    case 'hp':
      return effect.amount < 0
        ? `Lose ${Math.abs(effect.amount)} HP${(state.run?.hp ?? 0) <= Math.abs(effect.amount) ? ' — fatal now' : ''}`
        : `Heal ${effect.amount} HP`;
    case 'iron':
      return effect.amount < 0 ? `Pay ${Math.abs(effect.amount)} Iron` : `Gain ${effect.amount} Iron`;
    case 'max_hp':
      return effect.amount < 0
        ? `Lose ${Math.abs(effect.amount)} Max HP`
        : `Gain ${effect.amount} Max HP`;
    case 'add_card':
      return `Add ${content.cards[effect.cardDefinitionId]?.name ?? 'a card'}`;
    case 'choose_upgrade':
      return 'Choose one card to upgrade';
    case 'choose_remove':
      return 'Choose one card to remove';
    case 'repair_all':
      return 'Repair all Gear';
    case 'add_relic': {
      const name = content.relics[effect.relicId]?.name ?? 'a relic';
      return state.run?.relics.includes(effect.relicId)
        ? `Exchange duplicate ${name} for 15 Iron`
        : `Gain ${name}`;
    }
  }
}

function EventView({ state, onCommand }: { state: GameState; onCommand: CommandHandler }) {
  const event = state.event ? content.events[state.event.eventId] : undefined;
  if (!event) return null;
  const pendingAction = state.event?.pendingAction;
  const selectableCards = pendingAction === 'upgrade'
    ? state.run?.deck.filter((card) => !card.upgraded && content.cards[card.definitionId]?.upgrade) ?? []
    : state.run?.deck ?? [];

  return (
    <div className="screen system-screen event-screen">
      <GameHeader state={state} eyebrow="Off the supply line" />
      <main id="main-content" className="system-layout system-layout--event">
        <section className="event-panel" aria-labelledby="event-title">
          <span className="system-glyph" aria-hidden="true"><i /><i /></span>
          {pendingAction ? (
            <>
              <p className="kicker">The price is yours to name</p>
              <h1 id="event-title">Choose a card to {pendingAction}</h1>
              <p className="event-panel__body">
                {pendingAction === 'upgrade'
                  ? 'Select the technique that should return sharper.'
                  : 'Select the technique you are willing to leave behind.'}
              </p>
              <div className="event-card-selection" role="group" aria-label={`Cards to ${pendingAction}`}>
                {selectableCards.map((card) => (
                  <CardView
                    key={card.id}
                    definition={resolveCardDefinition(card, content)}
                    instance={card}
                    choice
                    upgradePreview={pendingAction === 'upgrade'}
                    onClick={() => onCommand({ type: 'choose_event_card', cardId: card.id })}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="kicker">Something waits off the rail</p>
              <h1 id="event-title">{event.title}</h1>
              <p className="event-panel__body">{event.body}</p>
              <div className="event-choices" role="group" aria-label="Event choices">
                {event.choices.map((choice) => {
                  const unavailableReason = getEventChoiceUnavailableReason(state, choice, content);
                  const unavailableId = `event-choice-${choice.id}-unavailable`;
                  return (
                    <button
                      className="event-choice"
                      type="button"
                      key={choice.id}
                      disabled={Boolean(unavailableReason)}
                      aria-describedby={unavailableReason ? unavailableId : undefined}
                      onClick={() => onCommand({ type: 'choose_event', choiceId: choice.id })}
                    >
                      <strong>{choice.label}</strong>
                      <span>{choice.outcome}</span>
                      {choice.effects.length > 0 ? (
                        <small className="event-choice__effects">
                          {choice.effects.map((effect, index) => (
                            <span key={`${effect.kind}-${index}`}>{describeEventEffect(effect, state)}</span>
                          ))}
                        </small>
                      ) : <small className="event-choice__effects"><span>No mechanical effect</span></small>}
                      {unavailableReason ? (
                        <small className="event-choice__unavailable" id={unavailableId}>
                          Unavailable: {unavailableReason}
                        </small>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function TraderView({ state, onCommand }: { state: GameState; onCommand: CommandHandler }) {
  const run = state.run;
  const shop = state.shop;
  if (!run || !shop) return null;
  const gear = gearCards(state);

  return (
    <div className="screen system-screen trader-screen">
      <GameHeader state={state} eyebrow="Field Quartermaster" />
      <main id="main-content" className="market-layout">
        <section className="market-heading" aria-labelledby="market-title">
          <p className="kicker">Recovery stores</p>
          <h1 id="market-title">Everything has already been used</h1>
          <p>The quartermaster has kept a dry cache for anyone still returning from the line. Trade salvaged Iron for supplies; every cylinder stays sealed.</p>
          <strong className="market-purse">{run.iron} Iron available</strong>
        </section>

        <section className="market-stock" aria-labelledby="stock-title">
          <div className="section-heading">
            <span><small>Salvaged techniques</small><h2 id="stock-title">Cards</h2></span>
          </div>
          <div className="market-card-grid">
            {shop.cards.map((offer) => {
              const definition = content.cards[offer.cardDefinitionId];
              return definition ? (
                <div className={`market-offer${offer.sold ? ' is-sold' : ''}`} key={offer.id}>
                  <CardView
                    definition={definition}
                    choice
                    disabled={offer.sold || run.iron < offer.price}
                    contextLabel={`${offer.price} Iron`}
                    disabledMessage={offer.sold ? 'Sold' : run.iron < offer.price ? 'Insufficient Iron' : undefined}
                    onClick={() => onCommand({ type: 'shop_buy_card', offerId: offer.id })}
                  />
                  <strong>{offer.sold ? 'Sold' : `${offer.price} Iron`}</strong>
                </div>
              ) : null;
            })}
          </div>
        </section>

        {shop.relic ? (
          <section className="market-relic" aria-labelledby="market-relic-title">
            <p className="kicker">Recovered field equipment</p>
            <img className="relic-art" src={`/art/cathedris/relics/${shop.relic.relicId}.webp`} alt="" />
            <h2 id="market-relic-title">{content.relics[shop.relic.relicId]?.name}</h2>
            <p>{content.relics[shop.relic.relicId]?.description}</p>
            <button
              className="secondary-action"
              type="button"
              disabled={shop.relic.sold || run.iron < shop.relic.price}
              onClick={() => onCommand({ type: 'shop_buy_relic' })}
            >
              {shop.relic.sold ? 'Sold' : `Buy relic · ${shop.relic.price} Iron`}
            </button>
          </section>
        ) : null}

        <section className="market-services" aria-labelledby="services-title">
          <div className="section-heading"><span><small>Workshop</small><h2 id="services-title">Services</h2></span></div>
          <div className="service-columns">
            <div>
              <h3>Repair Gear · {shop.repairPrice} Iron</h3>
              {gear.map((card) => {
                const definition = resolveCardDefinition(card, content);
                const maximum = definition.gear?.maxDurability ?? 0;
                const damaged = (card.durability ?? 0) < maximum;
                return (
                  <button
                    className="service-row"
                    type="button"
                    key={card.id}
                    disabled={!damaged || run.iron < shop.repairPrice}
                    onClick={() => onCommand({ type: 'shop_repair', cardId: card.id })}
                  >
                    <span>{definition.name}</span>
                    <strong>{card.durability ?? 0}/{maximum}</strong>
                  </button>
                );
              })}
            </div>
            <div>
              <h3>Remove a card · {shop.removalPrice} Iron</h3>
              <div className="removal-list" role="region" aria-label="Cards available for removal" tabIndex={0}>
                {run.deck.map((card) => {
                  const definition = resolveCardDefinition(card, content);
                  return (
                    <button
                      type="button"
                      key={card.id}
                      disabled={shop.removalUsed || run.iron < shop.removalPrice || run.deck.length <= 5}
                      onClick={() => onCommand({ type: 'shop_remove', cardId: card.id })}
                    >
                      Remove {definition.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <button className="primary-action market-leave" type="button" onClick={() => onCommand({ type: 'leave_trader' })}>
          Leave the market
        </button>
      </main>
    </div>
  );
}

function ForgeView({ state, onCommand }: { state: GameState; onCommand: CommandHandler }) {
  const run = state.run;
  if (!run) return null;
  const repairNeeded = gearCards(state).some((card) => {
    const definition = resolveCardDefinition(card, content);
    return (card.durability ?? 0) < (definition.gear?.maxDurability ?? 0);
  });
  const upgradeable = run.deck.filter((card) => !card.upgraded && content.cards[card.definitionId]?.upgrade);

  return (
    <div className="screen system-screen forge-screen">
      <GameHeader state={state} eyebrow="Field Workshop" />
      <main id="main-content" className="forge-layout">
        <section className="forge-heading" aria-labelledby="forge-title">
          <span className="forge-flame" aria-hidden="true"><i /></span>
          <p className="kicker">One shelter · one choice</p>
          <h1 id="forge-title">Spend the quiet well</h1>
          <p>The workshop is dry and its tools still work. Use the quiet to recover before following the next stretch of line.</p>
        </section>

        <section className="forge-actions" aria-label="Forge actions">
          <button
            type="button"
            disabled={run.hp >= run.maxHp}
            onClick={() => onCommand({ type: 'forge', action: 'heal' })}
          >
            <span className="forge-action__mark" aria-hidden="true">+</span>
            <strong>Bind wounds</strong>
            <span>Restore {Math.ceil(run.maxHp * 0.25)} HP</span>
            <small>{run.hp >= run.maxHp ? 'Already at full health' : `Current health ${run.hp}/${run.maxHp}`}</small>
          </button>
          <button
            type="button"
            disabled={!repairNeeded}
            onClick={() => onCommand({ type: 'forge', action: 'repair' })}
          >
            <span className="forge-action__mark" aria-hidden="true">◇</span>
            <strong>Repair the armory</strong>
            <span>Restore all Gear to full Durability</span>
            <small>{repairNeeded ? 'Damaged Gear detected' : 'All Gear is ready'}</small>
          </button>
        </section>

        <section className="forge-upgrades" aria-labelledby="upgrade-title">
          <div className="section-heading">
            <span><small>Temper one technique</small><h2 id="upgrade-title">Upgrade a card</h2></span>
            <span className="section-heading__note">This leaves the forge immediately</span>
          </div>
          <div className="forge-card-grid">
            {upgradeable.map((card) => (
              <CardView
                key={card.id}
                definition={resolveCardDefinition(card, content)}
                instance={card}
                choice
                upgradePreview
                onClick={() => onCommand({ type: 'forge', action: 'upgrade', cardId: card.id })}
              />
            ))}
          </div>
        </section>

        <button
          className="primary-action forge-leave"
          type="button"
          onClick={() => onCommand({ type: 'leave_forge' })}
        >
          Leave the forge unused
        </button>
      </main>
    </div>
  );
}

export function SystemScreen({
  state,
  system,
  onCommand,
}: {
  state: GameState;
  system: ExpeditionSystem;
  onCommand: CommandHandler;
}) {
  if (system === 'event') return <EventView state={state} onCommand={onCommand} />;
  if (system === 'trader') return <TraderView state={state} onCommand={onCommand} />;
  return <ForgeView state={state} onCommand={onCommand} />;
}
